/**
 * useGedcomParse Hook
 *
 * Manages GEDCOM file parsing workflow:
 * 1. Retrieve file from Supabase Storage
 * 2. Decode using detected encoding
 * 3. Tokenize GEDCOM text
 * 4. Build hierarchical tree
 * 5. Extract and parse INDI records
 *
 * Part of Phase 4 - Slice 2: Parser Integration
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/config/supabase';
import type { UploadedFileMetadata } from './useGedcomUpload';
import { tokenizeGedcom } from '../utils/tokenizer';
import { buildTree } from '../utils/treeBuilder';
import { processMultiLineTree } from '../utils/multiLineProcessor';
import { extractIndiRecords, type ParsedPerson } from '../parsers/indiParser';
import { extractFamRecords } from '../parsers/famParser';
import { resolveFamilies } from '../utils/crossReferenceResolver';
import type { ParsedFamily, ResolvedFamily } from '../types/GedcomFamily';
import { validateGedcomPreview } from '../utils/previewValidation';
import type { ValidationResult } from '../types/GedcomValidation';

// Parse state machine
export type ParseState = 'idle' | 'fetching' | 'parsing' | 'success' | 'error';

// Parse error types
export interface ParseError {
  code:
    | 'FETCH_FAILED'
    | 'DECODE_FAILED'
    | 'TOKENIZE_FAILED'
    | 'NO_PERSONS_FOUND'
    | 'UNKNOWN';
  message: string;
}

// Parse result
export interface ParseResult {
  persons: ParsedPerson[];
  families: ParsedFamily[];
  resolvedFamilies: ResolvedFamily[];
  skippedLines: number;
  totalLines: number;
  validation: ValidationResult;
}

// Hook interface
interface UseGedcomParseReturn {
  parseState: ParseState;
  parseFile: (metadata: UploadedFileMetadata) => Promise<ParseResult>;
  error: ParseError | null;
  result: ParseResult | null;
  reset: () => void;
}

/**
 * Custom hook for GEDCOM file parsing
 */
export const useGedcomParse = (): UseGedcomParseReturn => {
  const { t } = useTranslation();
  const [parseState, setParseState] = useState<ParseState>('idle');
  const [error, setError] = useState<ParseError | null>(null);
  const [result, setResult] = useState<ParseResult | null>(null);

  /**
   * Parse GEDCOM file from storage
   */
  const parseFile = async (metadata: UploadedFileMetadata): Promise<ParseResult> => {
    try {
      // Reset state
      setError(null);
      setResult(null);

      // Step 1: Fetch file from Supabase Storage
      setParseState('fetching');

      const { data: fileData, error: fetchError } = await supabase.storage
        .from('gedcom-temp')
        .download(metadata.storagePath);

      if (fetchError || !fileData) {
        const err: ParseError = {
          code: 'FETCH_FAILED',
          message: fetchError?.message || 'Failed to retrieve file from storage',
        };
        setError(err);
        setParseState('error');
        throw err;
      }

      // Step 2: Decode file using detected encoding
      setParseState('parsing');

      let text: string;
      try {
        const arrayBuffer = await fileData.arrayBuffer();

        // Map encoding to TextDecoder compatible label
        const encodingLabel = getTextDecoderEncoding(metadata.detectedEncoding.encoding);
        const decoder = new TextDecoder(encodingLabel);
        text = decoder.decode(arrayBuffer);
      } catch (decodeError) {
        const err: ParseError = {
          code: 'DECODE_FAILED',
          message: `Failed to decode file: ${decodeError instanceof Error ? decodeError.message : 'Unknown error'}`,
        };
        setError(err);
        setParseState('error');
        throw err;
      }

      // Step 3: Tokenize GEDCOM text
      let tokens;
      const totalLines = text.split(/\r?\n/).length;

      try {
        tokens = tokenizeGedcom(text);
      } catch (tokenizeError) {
        const err: ParseError = {
          code: 'TOKENIZE_FAILED',
          message: `Failed to tokenize GEDCOM: ${tokenizeError instanceof Error ? tokenizeError.message : 'Unknown error'}`,
        };
        setError(err);
        setParseState('error');
        throw err;
      }

      // Calculate skipped lines (malformed)
      const skippedLines = totalLines - tokens.length;

      // Step 4: Build tree structure
      const tree = buildTree(tokens);

      // Step 5: Process multi-line values (CONC/CONT)
      const processedTree = processMultiLineTree(tree);

      // Step 6: Extract INDI records
      const persons = extractIndiRecords(processedTree);

      // Check if any persons were found
      if (persons.length === 0) {
        const err: ParseError = {
          code: 'NO_PERSONS_FOUND',
          message: 'No individuals found in GEDCOM file',
        };
        setError(err);
        setParseState('error');
        throw err;
      }

      // Step 7: Extract FAM records
      const families = extractFamRecords(processedTree);

      // Step 8: Resolve family cross-references
      const resolvedFamilies = resolveFamilies(families, persons);

      // Step 9: Validate parsed data
      const validation = validateGedcomPreview(persons, resolvedFamilies, t);

      // Success
      const parseResult: ParseResult = {
        persons,
        families,
        resolvedFamilies,
        skippedLines,
        totalLines,
        validation,
      };

      setResult(parseResult);
      setParseState('success');

      return parseResult;
    } catch (err) {
      // Catch any unexpected errors
      if (error === null) {
        const unknownErr: ParseError = {
          code: 'UNKNOWN',
          message: err instanceof Error ? err.message : 'An unknown error occurred during parsing',
        };
        setError(unknownErr);
        setParseState('error');
      }
      throw err;
    }
  };

  /**
   * Reset hook state
   */
  const reset = () => {
    setParseState('idle');
    setError(null);
    setResult(null);
  };

  return {
    parseState,
    parseFile,
    error,
    result,
    reset,
  };
};

/**
 * Map GEDCOM encoding to TextDecoder compatible label
 */
const getTextDecoderEncoding = (
  encoding: 'UTF-8' | 'UTF-16' | 'CP1252' | 'UNKNOWN'
): string => {
  switch (encoding) {
    case 'UTF-8':
      return 'utf-8';
    case 'UTF-16':
      return 'utf-16le'; // Default to little-endian (most common)
    case 'CP1252':
      return 'windows-1252';
    case 'UNKNOWN':
      return 'utf-8'; // Fallback to UTF-8
  }
};
